import { Cell } from '@telegram-apps/telegram-ui';
import type { Generation } from '../types/generation';
import { formatImageUrl } from '../lib/imageUrl';
import { PRODUCT_IMAGE_BACKGROUND, PRODUCT_IMAGE_OBJECT_FIT } from '../lib/imagePolicy';
import { getGenerationSummary } from '../lib/generationLabels';
import { TgImage } from './TgImage';

const STATUS_ICON: Record<Generation['status'], string> = {
  pending: '⏳',
  preprocessing: '🎨',
  processing: '🎨',
  completed: '✅',
  failed: '❌',
  cancelled: '🚫',
};

function typeLabel(type: Generation['type']): string {
  if (type === 'catalog_clean') return 'Карточка товара';
  if (type === 'user_tryon') return 'Примерка';
  return 'Фотосессия';
}

function relativeRu(iso: string): string {
  const d = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.round((d - now) / 1000);
  const abs = Math.abs(diffSec);
  const rtf = new Intl.RelativeTimeFormat('ru', { numeric: 'auto' });
  if (abs < 60) return rtf.format(Math.round(diffSec), 'second');
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour');
  return rtf.format(Math.round(diffSec / 86400), 'day');
}

const ACTIVE_STATUSES = new Set<Generation['status']>(['pending', 'preprocessing', 'processing']);

export function GenerationCard({ generation, onClick }: { generation: Generation; onClick: () => void }) {
  const preview = generation.generatedImages[0]?.thumbnailUrl || generation.generatedImages[0]?.url || null;
  const productName = generation.product?.name || 'Без товара';
  const isActive = ACTIVE_STATUSES.has(generation.status);
  const summary = getGenerationSummary(generation);
  const routeParts = [summary.modeLabel || typeLabel(generation.type), ...summary.chips.slice(0, 2)];
  const subtitle = `${STATUS_ICON[generation.status]} ${routeParts.join(' · ')} · ${relativeRu(generation.createdAt)}`;

  const thumbnail = isActive ? (
    // Shimmer skeleton while generating
    <div
      className="gen-skeleton"
      style={{ width: 44, height: 56, borderRadius: 8, flexShrink: 0 }}
      aria-hidden="true"
    />
  ) : preview ? (
    <TgImage
      src={formatImageUrl(preview)}
      alt={productName}
      style={{ width: 44, height: 56, borderRadius: 8, objectFit: PRODUCT_IMAGE_OBJECT_FIT, background: PRODUCT_IMAGE_BACKGROUND, display: 'block' }}
      lazy={true}
    />
  ) : (
    <div
      style={{
        width: 44,
        height: 56,
        borderRadius: 8,
        background: PRODUCT_IMAGE_BACKGROUND,
        display: 'grid',
        placeItems: 'center',
        fontSize: 18,
        flexShrink: 0,
      }}
    >
      🎨
    </div>
  );

  return (
    <Cell
      subtitle={subtitle}
      after={thumbnail}
      onClick={onClick}
    >
      {/* Active indicator dot before product name */}
      {isActive && <span className="gen-pulse-dot" aria-hidden="true" />}
      {productName}
    </Cell>
  );
}
